import styles from "../chat-bubble.module.css";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import Image from "next/image";
import { ListingPayload } from "../../../../../../../../lib/chat-store/types";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { useEffect, useRef } from "react";
import ListingCard from "../listing-card";

export default function ListingBubble({
  payload,
  timestamp,
  sender_id,
  receiverCompleteName,
  receiverPhotoUrl,
}: {
  payload: ListingPayload;
  timestamp: number;
  sender_id: string;
  receiverCompleteName: string | undefined;
  receiverPhotoUrl: string | undefined;
}) {
  const { userData } = useUserAuthDataContext();
  const isMine = sender_id === userData.sub;
  const senderName =
    sender_id === userData.sub ? userData.complete_name : receiverCompleteName;
  const senderPhoto =
    sender_id === userData.sub ? userData.user_photo : receiverPhotoUrl;
  const swiperHintRef = useRef<HTMLDivElement | null>(null);

  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (!swiperHintRef.current || payload.listings?.length <= 1) return;

    const el = swiperHintRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            const wrapper = swiperHintRef.current?.querySelector(
              ".swiper-wrapper",
            ) as HTMLElement;

            if (!wrapper) return;
            wrapper.style.willChange = "transform";
            const runHint = () => {
              wrapper.animate(
                [
                  { transform: "translateX(0)" },
                  { transform: "translateX(-28px)" },
                  { transform: "translateX(0)" },
                ],
                {
                  duration: 550,
                  easing: "cubic-bezier(.4,0,.2,1)",
                },
              );
            };

            runHint();

            // second identical hint
            setTimeout(() => {
              runHint();

              // remove GPU hint after second animation finishes
              setTimeout(() => {
                wrapper.style.willChange = "auto";
              }, 560);
            }, 700);
          }, 500);

          observer.disconnect();
        }
      },
      {
        threshold: 0.7,
      },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`${styles.row} ${isMine ? styles.mine : styles.theirs} ${styles.listingBubble} ${
        payload.listings?.length > 0 ? styles.hasListings : ""
      }`}
    >
      <div className={styles.bubbleWrapper}>
        <div className={styles.infoAndTextWrapper}>
          <span className={styles.userInfo}>
            <span className={styles.userName}>{senderName}</span>
            <Image
              className={styles.avatar}
              src={senderPhoto || "/broken_image.webp"}
              width={24}
              height={24}
              alt={`${senderPhoto || "User"} profile photo`}
            />
          </span>
          {payload.listings?.length > 0 && (
            <div ref={swiperHintRef}>
              <Swiper slidesPerView="auto" spaceBetween={16}>
                {payload.listings.map((listing, index) => (
                  <SwiperSlide
                    key={listing.listing_id + index}
                    className={styles.listingSlide}
                  >
                    <ListingCard
                      listing={listing}
                      total={payload.listings?.length}
                      index={index + 1}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
          <span className={styles.text}>{payload["text"] ?? ""}</span>
        </div>
        <div className={styles.messageInfo}>
          {/* {isMine && <DoneAll className={styles.checkmark} />} */}
          <span className={styles.time}>{formatTime(timestamp)}</span>
        </div>
      </div>
    </div>
  );
}
